import { Treemap } from '@gameonly/core';
import { Grid } from '@mui/material';
import { ResponsiveTreeMap } from '@nivo/treemap';
import PdfLegend, { PdfLegendRow } from './PdfLegend';
import {
  SeedsTooltipComponent,
  SeedsTooltipRow,
} from './SeedsTooltipComponent';

const CustomTooltip = (props: any) => {
  const data = props.node.data;
  return (
    <SeedsTooltipComponent>
      <SeedsTooltipRow
        color={data.color}
        category={data.name}
        value={data.loc}
        unit={data.unit}
      />
    </SeedsTooltipComponent>
  );
};

const TreemapComponent = ({ result }: { result: Treemap }) => {
  const myColor = (node: any) => {
    return node.data.color;
  };
  if (!result?.data) {
    return null;
  }
  return (
    <ResponsiveTreeMap
      data={result?.data}
      identity="name"
      value="loc"
      valueFormat=".02s"
      margin={{ top: 10, right: 10, bottom: 10, left: 10 }}
      enableLabel={true}
      label={(node) => node.data.name}
      labelSkipSize={14}
      labelTextColor={{ from: 'color', modifiers: [['darker', 3]] }}
      enableParentLabel={true}
      parentLabel={(node) => node.data.name}
      parentLabelSize={18}
      parentLabelPosition="left"
      parentLabelPadding={6}
      parentLabelTextColor={{ from: 'color', modifiers: [['darker', 5]] }}
      leavesOnly={false}
      innerPadding={5}
      borderWidth={0}
      tooltip={CustomTooltip}
      colors={myColor}
      nodeOpacity={1}
      theme={{
        labels: {
          text: {
            fontSize: 14,
            fontWeight: 500,
          },
        },
      }}
      // layers={['nodes', CustomParentLabel]}
    />
  );
};

const CustomParentLabel = ({ nodes }: { nodes: any[] }) => {
  return (
    <>
      {nodes
        .filter((node) => node.treeDepth === 1)
        .map((node) => (
          <text
            key={node.id}
            x={node.x + 6}
            y={node.y + 20}
            fill="#000"
            fontSize="18"
            fontWeight="700"
            alignmentBaseline="middle"
          >
            {node.data.name}
          </text>
        ))}
    </>
  );
};

export const TreemapLegend = ({
  result,
  compact,
  color,
}: {
  result: Treemap;
  compact?: boolean;
  color?: string;
}) => {
  const treemapGroups = extractTreemapData(result);
  return (
    <PdfLegend compact={compact} color={color}>
      {treemapGroups.map((group: TreemapGroup, index: number) => (
        <Grid container key={index} spacing={0.5} mb={1}>
          {/* Parent Row */}
          {typeof group.parent.value !== 'undefined' &&
            group.parent.value > 0 && (
              <PdfLegendRow
                color={group.parent.color}
                category={group.parent.category}
                value={group.parent.value?.toString()}
                unit={group.parent.unit}
                compact={compact}
              />
            )}

          {/* Children Rows (styled differently) */}
          {group.children.length > 0 && (
            <Grid container item xs={12} pl={0} spacing={0.5}>
              {group.children.map(
                (child, childIndex) =>
                  typeof child.value !== 'undefined' &&
                  child.value > 0 && (
                    <PdfLegendRow
                      key={`${index}-${childIndex}`}
                      color={child.color}
                      nbColumns={3}
                      category={child.category}
                      value={child.value?.toString()}
                      unit={child.unit}
                      compact={true} // children always compact
                    />
                  ),
              )}
            </Grid>
          )}
        </Grid>
      ))}
    </PdfLegend>
  );
};

export default TreemapComponent;

interface TreemapResult {
  color: string | undefined;
  category: string | undefined;
  value: number | undefined;
  unit: string | undefined;
}

interface TreemapGroup {
  parent: TreemapResult;
  children: TreemapResult[];
}

const extractTreemapData = (treemap: Treemap): TreemapGroup[] => {
  const groupedResults: TreemapGroup[] = [];

  if (!treemap.data?.children) return groupedResults;

  for (const level1 of treemap.data.children) {
    const children: TreemapResult[] = [];
    let level1Value = 0;

    if (level1.children && level1.children.length > 0) {
      for (const level2 of level1.children) {
        level1Value += level2.loc ?? 0;

        children.push({
          color: level2.color,
          category: level2.name,
          value: level2.loc,
          unit: level2.unit ?? level1.unit ?? treemap.unit,
        });
      }
    } else {
      level1Value = level1.loc ?? 0;
    }

    const parent: TreemapResult = {
      color: level1.color,
      category: level1.name,
      value: level1Value,
      unit: level1.unit ?? treemap.unit,
    };

    groupedResults.push({ parent, children });
  }

  return groupedResults;
};
